import React, { useCallback, useEffect } from "react";
import { DraftAlbum, MultiTrackTableItem, useMusicApi } from "../../api/useMusicApi";
import { StringParam, useQueryParam } from "use-query-params";
import { useLoading } from "../../hooks/use-loading";
import { useAdvancedState } from "../../hooks/use-advanced-state";
import { EditDraftForm } from "./EditDraftForm";
import { toast } from "react-toastify";
import { Preloader } from "../../components/Preloader";
import { PartialNullable } from "../../types";
import { useGenres } from "../../hooks/use-genres";
import { useContractors } from "../../contexts/ContractorsContext";
import { AxiosError } from "axios";
import { useValidationErrors } from "../../hooks/use-validation-errors";
import { usePageHelper } from "../../hooks/use-page-helper";
import { prepareArtistsToRequest } from "../../services/ArtistService";
import { getArtistsWithMoreThatOneUniqueUrl } from "../../utils";
import { useModalConfirmAction } from "../../components/Modals/ModalConfirmActionContext";

export const AlbumEditDraftPage: React.FC = () => {
  const { t, routeParams: { id } } = usePageHelper("edit_draft");

  const [trackId] = useQueryParam("track_id", StringParam);
  const { loadings, startLoading, stopLoading } = useLoading({
    album: true,
    saveTracks: false,
    track: trackId != null,
    payment: trackId != null
  });

  const api = useMusicApi();
  const [album, setAlbum, updateAlbum] = useAdvancedState<DraftAlbum>({});
  const { genres } = useGenres();
  const { contractors } = useContractors();
  const { validationErrors, setValidationErrors } = useValidationErrors(album);
  const { showConfirmActionModal } = useModalConfirmAction();
  useEffect(() => {
    if (id != null) {
      void fetchAlbum(id);
    }
  }, [id]);

  const fetchAlbum = useCallback(async (id: string) => {
    try {
      startLoading("album");
      const res = await api.getAlbumDraft(id);
      setAlbum(res);
    } catch (e) {
    } finally {
      stopLoading("album");
    }
  }, []);

  const handleSaveDraft = useCallback(async (tracks: PartialNullable<MultiTrackTableItem>[]) => {
    try {
      startLoading("saveTracks");
      await api.postUpdateMultiTrack(id as string, tracks);
    } catch (e) {
      throw e;
    } finally {
      stopLoading("saveTracks");
    }
  }, [id]);
  
  const handleUpdateDraft = useCallback(async () => {
    try {
      const invalidMainArtistsEntries = getArtistsWithMoreThatOneUniqueUrl(t, album, album.trackrelease_set ?? []);
      if (invalidMainArtistsEntries.length > 0) {
        const description = invalidMainArtistsEntries.map(([key, values]) => {
          let title = `${key}: \n`;
          (values as any).forEach((v: any) => {
            title += `${v.track} – ${v.url}\n`;
          });
          return title;
        }).join("\n\n");

        const closeModalResult = await showConfirmActionModal(
          t("artists_equals_url_error"),
          description
        );

        if (!closeModalResult) {
          return;
        }
      }

      startLoading("album");
      delete album["trackrelease_set"];

      const res = await api.patchUpdateAlbumDraft(
        id as any,
        {
          ...album,
          genres: album.genres ?? [],
          country_meta: {
            ...(album.country_meta ?? {}),
            subtitle: album?.country_meta?.subtitle ?? { ALL: null, RU: null },
            artists: prepareArtistsToRequest(album?.country_meta ?? null)
          }
        } as any
      );
      setAlbum(res);
      toast.success(`${t("changes_save")}`);
    } catch (e) {
      setValidationErrors(
        ((e as AxiosError).response?.data as any)?.errors ?? [],
        album as any
      );
      toast.error(`${t("changes_not_save")}`);
    } finally {
      stopLoading("album");
    }
  }, [id, album]);


  return (
    <div className={"flex-col w-full"}>
      {loadings.album && <Preloader className={"my-10"} size={"5x"} />}
      {!loadings.album && (
        <EditDraftForm
          isDraft={true}
          title={t("edit_draft")}
          entity={album}
          isChangesPending={loadings.saveTracks}
          genres={genres}
          validationErrors={validationErrors}
          contractors={contractors}
          onChange={updateAlbum}
          onFileSelect={api.postUploadFile}
          onSaveAlbum={handleUpdateDraft}
          onSaveTracks={handleSaveDraft}
        />
      )}
    </div>
  );
};
